import React from "react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Slider } from "./ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Search, X, Shield, Users as UsersIcon, Gauge, Star, Pin, Home, Radio, RefreshCw, Loader2, HelpCircle } from "lucide-react";
import { useLatency } from "../context/LatencyContext";
import { useGame } from "../context/GameContext";

const Label = ({ children, testId }) => (
  <div className="hud-label mb-2" data-testid={testId}>
    {children}
  </div>
);

const ModifierChip = ({ label, active, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="font-mono-hud text-xs tracking-widest px-3 py-1.5 border transition-colors"
    style={{
      color: active ? "#000" : "var(--text-primary)",
      background: active ? "var(--primary-accent)" : "var(--bg-elevated)",
      borderColor: active ? "var(--primary-accent)" : "var(--border-strong)",
      borderRadius: 2,
    }}
  >
    {label}
  </button>
);

const selectStyle = {
  background: "var(--bg-elevated)",
  borderColor: "var(--border-strong)",
  color: "var(--text-primary)",
  borderRadius: 2,
};

// Segmented control: pick ping SOURCE for the whole list.
// SERVER ESTIMATE = raw server-reported ping (regional estimate for live
// sources). PING FROM MY HOME = baseline measured in this browser session
// (useLatency context) added to a per-region offset.
const PingSourceToggle = () => {
  const { enabled, toggle, baseline, remeasure } = useLatency();
  const [measuring, setMeasuring] = React.useState(false);

  const pickServer = () => { if (enabled) toggle(); };
  const pickHome = () => { if (!enabled) toggle(); };
  const onRemeasure = async () => {
    setMeasuring(true);
    try { await remeasure(); } finally { setMeasuring(false); }
  };

  const btnBase = "font-mono-hud text-xs tracking-widest px-2.5 py-1 border transition-colors inline-flex items-center gap-1.5";
  const active = {
    color: "#000",
    background: "var(--primary-accent)",
    borderColor: "var(--primary-accent)",
    borderRadius: 2,
  };
  const idle = {
    color: "var(--text-primary)",
    background: "var(--bg-elevated)",
    borderColor: "var(--border-strong)",
    borderRadius: 2,
  };
  return (
    <div className="flex items-center gap-1.5" data-testid="ping-source-toggle">
      <span className="hud-label hidden lg:inline mr-1">PING</span>
      <button
        type="button"
        onClick={pickServer}
        data-testid="ping-source-server"
        style={!enabled ? active : idle}
        className={btnBase}
        title="Server-reported regional estimate — same number for every player in your region"
      >
        <Radio className="w-3 h-3" /> SERVER-REPORTED
      </button>
      <button
        type="button"
        onClick={pickHome}
        data-testid="ping-source-home"
        style={enabled ? active : idle}
        className={btnBase}
        title={baseline != null ? `Round-trip time measured in your browser (baseline ${baseline}ms)` : "Measure round-trip from your browser"}
      >
        <Home className="w-3 h-3" />
        <span>FROM MY HOME</span>
        {enabled && baseline != null && (
          <span className="tabular-nums opacity-80">{baseline}ms</span>
        )}
      </button>
      <span
        data-testid="ping-source-help"
        className="inline-flex items-center justify-center cursor-help"
        title={"SERVER-REPORTED: regional estimate from the server (same for everyone in your region).\nFROM MY HOME: actual round-trip time measured in your browser session — reflects your real connection."}
        style={{ color: "var(--text-dim)" }}
      >
        <HelpCircle className="w-3.5 h-3.5" />
      </span>
      {enabled && (
        <button
          type="button"
          onClick={onRemeasure}
          disabled={measuring}
          data-testid="ping-source-remeasure"
          className="font-mono-hud text-[10px] tracking-widest px-1.5 py-1 border inline-flex items-center gap-1"
          style={{
            color: measuring ? "var(--text-dim)" : "var(--secondary-accent)",
            borderColor: "var(--border-strong)",
            background: "var(--bg-elevated)",
            borderRadius: 2,
          }}
          title="Re-run the 5-sample baseline measurement"
        >
          {measuring ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          {measuring ? "MEASURING" : "RE-MEASURE"}
        </button>
      )}
    </div>
  );
};

const ServerFilters = ({ filters, setFilters, resultCount, options }) => {
  const update = (patch) => setFilters((f) => ({ ...f, ...patch, page: 1 }));
  const { game } = useGame();
  const regions = options?.regions || [];
  const modes = options?.modes || [];
  const maps = options?.maps || [];
  const modifiers = options?.modifiers || [];
  const favOnly = !!filters.favorites_only;
  // Shared style applied to all filters that are ignored while FAVORITES ONLY
  // is on. Keeps them visible (so users know they're parked, not deleted)
  // but non-interactive.
  const dimmedStyle = favOnly
    ? { opacity: 0.4, pointerEvents: "none", filter: "saturate(0.4)" }
    : undefined;

  const reset = () =>
    setFilters({
      q: "",
      region: "all",
      mode: "all",
      map: "all",
      modifier: "all",
      official: "all",
      min_players: 0,
      max_ping: 300,
      online_only: true,
      favorites_only: false,
      pin_favorites: true,
      sort: "players",
      order: "desc",
      page: 1,
      page_size: 25,
    });

  return (
    <div className="tac-card p-4 lg:p-5 space-y-5 rise" data-testid="filter-bar">
      <span className="corner-tr" />
      <span className="corner-bl" />

      <div style={dimmedStyle} aria-disabled={favOnly} data-testid="search-row">
        <Label testId="filter-label-search">SEARCH SERVER NAME / ID</Label>
        <div className="relative">
          <Search className="w-4 h-4 absolute top-1/2 left-3 -translate-y-1/2 pointer-events-none" style={{ color: "var(--text-secondary)" }} />
          <Input
            data-testid="search-input"
            placeholder="e.g. HARDCORE, Hypixel, de_dust2, WD-1024..."
            value={filters.q}
            onChange={(e) => update({ q: e.target.value })}
            className="pl-9 pr-9 font-mono-hud tracking-wide"
            style={{ background: "var(--bg-main)", borderColor: "var(--border-strong)", color: "var(--text-primary)", borderRadius: 2, height: 42 }}
          />
          {filters.q && (
            <button type="button" data-testid="search-clear" onClick={() => update({ q: "" })} className="absolute top-1/2 right-2 -translate-y-1/2 p-1 rounded" style={{ color: "var(--text-secondary)" }} aria-label="Clear search">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" style={dimmedStyle} aria-disabled={favOnly} data-testid="dropdown-filters-row">
        <div>
          <Label>REGION</Label>
          <Select value={filters.region} onValueChange={(v) => update({ region: v })}>
            <SelectTrigger data-testid="filter-region" style={selectStyle}><SelectValue placeholder="All regions" /></SelectTrigger>
            <SelectContent style={selectStyle}>
              <SelectItem value="all">All regions</SelectItem>
              {regions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>GAME MODE</Label>
          <Select value={filters.mode} onValueChange={(v) => update({ mode: v })}>
            <SelectTrigger data-testid="filter-mode" style={selectStyle}><SelectValue placeholder="All modes" /></SelectTrigger>
            <SelectContent style={selectStyle}>
              <SelectItem value="all">All modes</SelectItem>
              {modes.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>MAP</Label>
          <Select value={filters.map} onValueChange={(v) => update({ map: v })}>
            <SelectTrigger data-testid="filter-map" style={selectStyle}><SelectValue placeholder="All maps" /></SelectTrigger>
            <SelectContent style={selectStyle}>
              <SelectItem value="all">All maps</SelectItem>
              {maps.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>OFFICIAL / COMMUNITY</Label>
          <Select value={filters.official} onValueChange={(v) => update({ official: v })}>
            <SelectTrigger data-testid="filter-official" style={selectStyle}><SelectValue /></SelectTrigger>
            <SelectContent style={selectStyle}>
              <SelectItem value="all">All servers</SelectItem>
              <SelectItem value="official"><span className="inline-flex items-center gap-2"><Shield className="w-3.5 h-3.5" /> Official only</span></SelectItem>
              <SelectItem value="community">Community only</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {modifiers.length > 0 && (
        <div data-testid="modifier-filter-row" style={dimmedStyle} aria-disabled={favOnly}>
          <Label>MODIFIER</Label>
          <div className="flex flex-wrap gap-2">
            <ModifierChip
              label="ALL"
              active={!filters.modifier || filters.modifier === "all"}
              onClick={() => update({ modifier: "all" })}
              testId="modifier-chip-all"
            />
            {modifiers.map((m) => (
              <ModifierChip
                key={m}
                label={m}
                active={filters.modifier === m}
                onClick={() => update({ modifier: m })}
                testId={`modifier-chip-${m}`}
              />
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" style={dimmedStyle} aria-disabled={favOnly} data-testid="sliders-row">
        <div data-testid="filter-min-players">
          <div className="flex items-center justify-between mb-3">
            <Label><UsersIcon className="w-3 h-3 inline mr-1" />MIN PLAYERS</Label>
            <span className="font-mono-hud text-sm" style={{ color: "var(--primary-accent)" }}>{filters.min_players}+</span>
          </div>
          <Slider value={[filters.min_players]} onValueChange={([v]) => update({ min_players: v })} min={0} max={500} step={5} />
        </div>
        <div data-testid="filter-max-ping">
          <div className="flex items-center justify-between mb-3">
            <Label><Gauge className="w-3 h-3 inline mr-1" />MAX PING</Label>
            <span className="font-mono-hud text-sm" style={{ color: "var(--primary-accent)" }}>≤ {filters.max_ping}ms</span>
          </div>
          <Slider value={[filters.max_ping]} onValueChange={([v]) => update({ max_ping: v })} min={20} max={400} step={5} />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 pt-2 border-t" style={{ borderColor: "var(--border-color)" }}>
        <div className="flex items-center gap-2">
          <Label>SORT BY</Label>
          <Select value={filters.sort} onValueChange={(v) => update({ sort: v })}>
            <SelectTrigger data-testid="sort-select" style={selectStyle} className="w-[150px] h-9"><SelectValue /></SelectTrigger>
            <SelectContent style={selectStyle}>
              <SelectItem value="players">Players</SelectItem>
              <SelectItem value="ping">Ping</SelectItem>
              <SelectItem value="name">Name</SelectItem>
              <SelectItem value="region">Region</SelectItem>
              <SelectItem value="map">Map</SelectItem>
              <SelectItem value="mode">Mode</SelectItem>
              <SelectItem value="max_players">Capacity</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.order} onValueChange={(v) => update({ order: v })}>
            <SelectTrigger data-testid="order-select" style={selectStyle} className="w-[110px] h-9"><SelectValue /></SelectTrigger>
            <SelectContent style={selectStyle}>
              <SelectItem value="desc">Desc</SelectItem>
              <SelectItem value="asc">Asc</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2 ml-auto flex-wrap">
          <PingSourceToggle />
          <span className="w-px h-5 mx-1" style={{ background: "var(--border-strong)" }} />
          <div className="flex items-center gap-2" title="Pin favorite servers to the top of every list">
            <Switch
              data-testid="pin-favorites-toggle"
              checked={filters.pin_favorites !== false}
              onCheckedChange={(v) => update({ pin_favorites: v })}
            />
            <span className="hud-label flex items-center gap-1">
              <Pin className="w-3 h-3" style={{ color: "var(--primary-accent)" }} /> PIN FAVS
            </span>
          </div>
          <div className="flex items-center gap-2" title="Show only your favorite servers for the current game (ignores other filters)">
            <Switch
              data-testid="favorites-only-toggle"
              checked={filters.favorites_only}
              onCheckedChange={(v) => update({ favorites_only: v })}
            />
            <span className="hud-label flex items-center gap-1">
              <Star className="w-3 h-3" style={{ color: "var(--highlight)" }} /> FAVORITES ONLY
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              data-testid="online-only-toggle"
              checked={filters.online_only}
              onCheckedChange={(v) => update({ online_only: v })}
            />
            <span className="hud-label">ONLINE ONLY</span>
          </div>
        </div>

        <Button variant="ghost" data-testid="reset-filters" onClick={reset} className="font-mono-hud tracking-widest" style={{ color: "var(--secondary-accent)", borderRadius: 2 }}>
          <X className="w-4 h-4 mr-1" /> RESET
        </Button>
        {resultCount !== undefined && (
          <span className="hud-caption">
            <span style={{ color: "var(--primary-accent)" }}>{resultCount}</span> MATCH{resultCount === 1 ? "" : "ES"}
          </span>
        )}
      </div>

      {favOnly && (
        <div
          className="flex items-start gap-2 border px-3 py-2"
          style={{
            borderColor: "color-mix(in srgb, var(--highlight) 50%, transparent)",
            background: "color-mix(in srgb, var(--highlight) 10%, transparent)",
            borderRadius: 2,
          }}
          data-testid="favorites-ignore-notice"
        >
          <Star className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: "var(--highlight)" }} />
          <span className="text-xs" style={{ color: "var(--text-primary)" }}>
            Showing all your{" "}
            <span className="font-bold" style={{ color: "var(--highlight)" }}>
              {game?.name || "game"}
            </span>{" "}
            favorites — region, mode, map, official filter and sliders are
            temporarily ignored. Toggle it off to narrow by filters again.
          </span>
        </div>
      )}
    </div>
  );
};

export default ServerFilters;
